import { useMemo, useState } from 'react';

import { useDebounce, useView } from '@/hooks';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@/ui';

import type { CoachOption, User } from '@/types';
import type { ChangeEvent, FC, SyntheticEvent } from 'react';

import { useBulkCreateCoachAssignmentsMutation } from '@/store/api/coachAssignmentApi';
import { useGetCoachProfilesQuery } from '@/store/api/userApi';

interface AddCoachModalProps {
  client: User;
  close: () => void;
  excludedCoachIds: string[];
  isOpen: boolean;
}

type DeliveryMode = 'now' | 'scheduled';

const MAX_COACH_SELECTION = 3;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 50;

const getBrowserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const getErrorMessage = (error: unknown) => {
  const maybeError = error as {
    data?: { errors?: string[] | Record<string, string[]>; error?: string };
    message?: string;
  };
  const errors = maybeError.data?.errors;

  if (Array.isArray(errors)) {
    return errors.join(', ');
  }

  if (errors && typeof errors === 'object') {
    return Object.entries(errors)
      .map(([field, messages]) => `${field} ${messages.join(', ')}`)
      .join(', ');
  }

  return (
    maybeError.data?.error || maybeError.message || 'Unable to add coaches'
  );
};

// TODO: Most of these components should live in components, not in pages
const AddCoachModal: FC<AddCoachModalProps> = ({
  client,
  close,
  excludedCoachIds,
  isOpen,
}) => {
  const { refetchSelectedUser } = useView();
  const [processing, setProcessing] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [selectedCoaches, setSelectedCoaches] = useState<CoachOption[]>([]);
  const [searchText, setSearchText] = useState('');
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const timezone = useMemo(getBrowserTimezone, []);
  const debouncedSearch = useDebounce(searchText, () => {}, SEARCH_DEBOUNCE_MS);

  const [bulkCreateCoachAssignments] = useBulkCreateCoachAssignmentsMutation();
  const { data: coaches = [], isFetching: isLoading } =
    useGetCoachProfilesQuery(
      {
        limit: SEARCH_LIMIT,
        page: 1,
        search: debouncedSearch.trim(),
      },
      { skip: !isOpen },
    );

  const selectedCoachIds = selectedCoaches.map(({ id }) => id);
  const options = coaches
    .filter(({ user }) => !excludedCoachIds.includes(user.id))
    .filter(({ user }) => !selectedCoachIds.includes(user.id))
    .map(({ user }) => {
      const { fullName, email, id } = user;
      return { email, id, label: fullName };
    });

  const scheduledAt = useMemo(() => {
    if (!scheduledDate || !scheduledTime) {
      return null;
    }

    const date = new Date(`${scheduledDate}T${scheduledTime}`);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [scheduledDate, scheduledTime]);

  const scheduledInvalid =
    sendEmail &&
    deliveryMode === 'scheduled' &&
    (!scheduledAt || scheduledAt <= new Date());

  const confirmDisabled =
    selectedCoaches.length === 0 ||
    selectedCoaches.length > MAX_COACH_SELECTION ||
    isLoading ||
    processing ||
    scheduledInvalid;

  const onChange = (
    _event: SyntheticEvent<Element, Event>,
    value: CoachOption | null,
  ) => {
    if (!value || selectedCoaches.length >= MAX_COACH_SELECTION) {
      return;
    }

    setSelectedCoaches((current) =>
      current.some(({ id }) => id === value.id) ? current : [...current, value],
    );
    setSearchText('');
    setErrorMessage('');
  };

  const onInputChange = (
    _event: SyntheticEvent<Element, Event>,
    value: string,
    reason: string,
  ) => {
    if (reason === 'input' || reason === 'clear') {
      setSearchText(value);
    }
  };

  const removeCoach = (coachId: string) => {
    setSelectedCoaches((current) =>
      current.filter((coach) => coach.id !== coachId),
    );
  };

  const reset = () => {
    setSendEmail(true);
    setProcessing(false);
    setSelectedCoaches([]);
    setSearchText('');
    setDeliveryMode('now');
    setScheduledDate('');
    setScheduledTime('');
    setErrorMessage('');
  };

  const closeModal = () => {
    close();
    reset();
  };

  const onAdd = async () => {
    try {
      setProcessing(true);
      setErrorMessage('');

      await bulkCreateCoachAssignments({
        coachIds: selectedCoachIds,
        coacheeId: client.id,
        delivery: sendEmail
          ? {
              mode: deliveryMode,
              scheduledAt:
                deliveryMode === 'scheduled'
                  ? scheduledAt?.toISOString()
                  : undefined,
              timezone,
            }
          : undefined,
        sendEmail,
      }).unwrap();
      await refetchSelectedUser();
      closeModal();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  const onDeliveryModeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDeliveryMode(event.target.value as DeliveryMode);
  };

  return (
    <Dialog maxWidth="md" open={isOpen} fullWidth onClose={closeModal}>
      <DialogContent>
        <Grid spacing={4} container>
          <Grid md={5} xs={12} item>
            <Typography className="mb-4" variant="h4">
              Add Coach
            </Typography>
            <Typography className="mb-2" variant="body1">
              Select the coach(es) to assign to {client.fullName}.
            </Typography>
          </Grid>
          <Grid className="flex flex-col gap-4" md={7} xs={12} item>
            <Autocomplete
              filterOptions={(x) => x}
              getOptionLabel={(option) => `${option.label} (${option.email})`}
              inputValue={searchText}
              loading={isLoading}
              noOptionsText={
                selectedCoaches.length >= MAX_COACH_SELECTION
                  ? 'Maximum 3 coaches selected'
                  : 'No coaches found'
              }
              options={options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  helperText="Type to search all coaches; blank shows the first 50."
                  label="Coach"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Typography color="text.primary" variant="body1">
                    {option.label}
                  </Typography>
                  <Typography
                    className="ml-1"
                    color="text.disabled"
                    variant="caption"
                  >
                    ({option.email})
                  </Typography>
                </Box>
              )}
              value={null}
              onChange={onChange}
              onInputChange={onInputChange}
            />

            {selectedCoaches.length > 0 && (
              <div className="flex flex-col gap-2">
                {selectedCoaches.map((coach) => (
                  <div
                    key={coach.id}
                    className="flex min-h-12 items-center justify-between rounded-lg border border-sage-300 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <Typography className="truncate" variant="body1">
                        {coach.label}
                      </Typography>
                      <Typography
                        className="truncate"
                        color="text.disabled"
                        variant="caption"
                      >
                        {coach.email}
                      </Typography>
                    </div>
                    <IconButton
                      aria-label={`Remove ${coach.label}`}
                      size="small"
                      onClick={() => {
                        removeCoach(coach.id);
                      }}
                    >
                      <Icon>close</Icon>
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </Grid>
          <Grid md={5} xs={12} item>
            <Typography className="mb-4" variant="h4">
              Send email
            </Typography>
            <Typography className="mb-2" variant="body1">
              If this option is selected, both Coach and Member will receive
              introductory email notifications.
            </Typography>
          </Grid>
          <Grid className="flex flex-col gap-4" md={7} xs={12} item>
            <div className="flex min-h-14 items-center justify-between rounded-lg bg-sage-100 p-2">
              <div className="flex gap-2">
                <Icon>email</Icon>
                <Typography variant="body1">Send introductory email</Typography>
              </div>

              <Switch
                checked={sendEmail}
                onChange={() => {
                  setSendEmail((current) => !current);
                }}
              />
            </div>

            <RadioGroup value={deliveryMode} onChange={onDeliveryModeChange}>
              <FormControlLabel
                control={<Radio />}
                disabled={!sendEmail}
                label="Now"
                value="now"
              />
              <FormControlLabel
                control={<Radio />}
                disabled={!sendEmail}
                label="Scheduled"
                value="scheduled"
              />
            </RadioGroup>

            {sendEmail && deliveryMode === 'scheduled' && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <TextField
                  error={scheduledInvalid && !scheduledDate}
                  InputLabelProps={{ shrink: true }}
                  label="Date"
                  type="date"
                  value={scheduledDate}
                  onChange={(event) => {
                    setScheduledDate(event.target.value);
                  }}
                />
                <TextField
                  error={scheduledInvalid && !scheduledTime}
                  helperText={timezone}
                  InputLabelProps={{ shrink: true }}
                  label="Time"
                  type="time"
                  value={scheduledTime}
                  onChange={(event) => {
                    setScheduledTime(event.target.value);
                  }}
                />
              </div>
            )}

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button disabled={processing} variant="outlined" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          disabled={confirmDisabled}
          loading={processing}
          variant="contained"
          onClick={onAdd}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCoachModal;
